import { CollaborationListItem, Request } from '@99designs/graph-utils/types';
import { useListingFreelanceCollaborationsQuery } from '../queries/ListingCollaborations.generated';
import { useListingRequestsForUserQuery } from '../queries/ListingRequests.generated';
import { useListingWorkItemsQuery } from '../queries/ListingWorkItems.generated';
import { STANDARD_PREVIEW_IMAGE } from '../utils/constants';
import { LegacyRequest } from './useCombinedProjects';

export function useProjectData() {
    const workItems = useListingWorkItemsQuery({
        variables: { input: { status: 'ALL', page: 1, tenant: 'vistaprint' } },
        errorPolicy: 'ignore',
    });
    const freelanceCollabsListItems = useListingFreelanceCollaborationsQuery({
        errorPolicy: 'ignore',
    });
    const error = workItems.error;
    const requestListItems = useListingRequestsForUserQuery({
        variables: {
            input: {},
        },
        errorPolicy: 'ignore',
    });
    const loading = requestListItems.loading;

    const extractedCollaborations: CollaborationListItem[] = [];
    const jobs: Request[] = [];
    const legacy: LegacyRequest[] = [];
    const processing: Request[] = [];

    if (!requestListItems.data?.requestsForUser) {
        return {
            workItems: workItems.data?.yourWorkItems.items ?? [],
            designRequests: [],
            collaborations: [],
            jobs: [],
            legacy: legacy,
            processing: [],
            error,
            loading,
        };
    }

    const requests = requestListItems.data.requestsForUser.requests;
    requests.forEach((request) => {
        if (request.state === 'PENDING_FULFILMENT') {
            processing.push(request as Request);
        } else if (request.state === 'MATCHING' && request.job != null) {
            // only show matching items for now
            jobs.push(request as Request);
        } else if (request.collaborationListItem) {
            extractedCollaborations.push(request.collaborationListItem);
        } else if (request.facts?.isLegacyImport) {
            const legacyReq = mapToLegacyRequest(request as Request);
            legacy.push(legacyReq);
        }
    });

    const freelanceCollabs = freelanceCollabsListItems.data?.collaborationsForFreelancer ?? [];

    return {
        workItems: workItems.data?.yourWorkItems.items ?? [],
        collaborations: [...extractedCollaborations, ...freelanceCollabs],
        jobs, // filtered to only show matched requests
        legacy,
        processing,
        error,
        loading,
    };
}

function mapToLegacyRequest(request: Request): LegacyRequest {
    const lastLegacyDownload = request.facts?.legacyDownloads?.slice(-1)[0];
    const previewRef = lastLegacyDownload?.previewUrl ?? STANDARD_PREVIEW_IMAGE;

    const downloads =
        request.facts?.legacyDownloads
            ?.map((d) => d.downloadUrl)
            .filter((url): url is string => url !== undefined) ?? [];

    return {
        publicId: request.publicId,
        type: request.facts?.legacyRequestType ?? '',
        createdAt: request.createdAt,
        title: request.facts?.legacyProductName ?? '',
        previewRef,
        downloads,
    };
}
